import type * as type from "@vTypes";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import useGradeConfig from "@hooks/useGradeConfig";
import { useQueryClient } from "@tanstack/react-query";
import { images } from "@img/imagesMap";
import useNurses from "@hooks/useNurses";
import type { NurseWithRelation } from "@vTypes/nurse-type";
import type {
  ShiftType,
  GradeSavePayload,
  GradeShiftCounts,
  GradeData,
} from "@utils/grade-util";
import {
  MIN_ASSIGNABLE_GRADES_COUNT,
  transformGradeData,
} from "@utils/grade-util";
import { validateShiftCoverage } from "@utils/shift-coverage-validation";
import {
  linkPreceptorRelations,
  unlinkPreceptorRelations,
} from "@utils/nurse-util";
import { useMutation } from "@tanstack/react-query";
import useThrottle from "@hooks/useThrottle";
import CustomModal from "@/components/modal/CustomModal";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { QUERY_BASE_KEY } from "@/constants/query-key";
import { useIsAdmin } from "@hooks/useAuth";
import httpClient from "@config/axios";
import { MSG } from "@constants/MSG.ts";
import { toast } from "@/components/toast/CustomToast";
import { GradeTransferTables } from "./GradeTransferTables";
import {
  GradeShiftConfigModal,
  type GradeShiftGridRowDisplay,
} from "./GradeShiftConfigModal";
import ActionButton from "@/components/ui/button/btn-action";
import useRosterConfig from "@hooks/useRosterConfig";
import type { GradeNamesMap } from "@vTypes/gradeConfig";
import GridModalHeader from "@/components/ui/modal/grid-modal-header";
import GridModalTitle from "@/components/ui/modal/grid-modal-title";

type Grade = {
  id: string;
  label: string;
  count: number;
};

type GradeBundleState = {
  nursesByGrade: Record<string, NurseWithRelation[]>;
  unassignedNurses: NurseWithRelation[];
};

type GradeBundleAction =
  | { type: "apply"; fn: (prev: GradeBundleState) => GradeBundleState }
  | {
      type: "move_nurses";
      nurseIds: string[];
      fromKey: string;
      toKey: string;
    };

function gradeBundleReducer(
  state: GradeBundleState,
  action: GradeBundleAction
): GradeBundleState {
  if (action.type === "apply") {
    return action.fn(state);
  }

  const { nurseIds, fromKey, toKey } = action;
  if (!nurseIds.length || fromKey === toKey) return state;

  const fromPool =
    fromKey === "unassigned"
      ? state.unassignedNurses
      : (state.nursesByGrade[fromKey] ?? []);

  const moving = fromPool.filter((n) => nurseIds.includes(n.nurse_id));
  if (!moving.length) return state;

  const restFrom = fromPool.filter((n) => !nurseIds.includes(n.nurse_id));

  const nursesByGrade = { ...state.nursesByGrade };
  let unassignedNurses = [...state.unassignedNurses];

  if (fromKey === "unassigned") {
    unassignedNurses = restFrom;
  } else {
    nursesByGrade[fromKey] = restFrom;
  }

  const destGradeNum =
    toKey === "unassigned" ? 0 : parseInt(toKey.replace("grade", ""), 10);

  const moved = moving.map((n) => ({
    ...n,
    grade: destGradeNum,
  }));

  if (toKey === "unassigned") {
    unassignedNurses = [...unassignedNurses, ...moved];
  } else {
    nursesByGrade[toKey] = [...(nursesByGrade[toKey] ?? []), ...moved];
  }

  return { nursesByGrade, unassignedNurses };
}

function buildInitialNursesByGrade(
  initialKeys: string[],
  nurseDataWithRelation: NurseWithRelation[] | undefined
): Record<string, NurseWithRelation[]> {
  const initial: Record<string, NurseWithRelation[]> = {};
  initialKeys.forEach((k) => {
    initial[k] = [];
  });

  nurseDataWithRelation?.forEach((nurse) => {
    const gId = nurse.grade ? `grade${nurse.grade}` : null;
    if (gId) {
      if (!initial[gId]) initial[gId] = [];
      initial[gId].push(nurse);
    }
  });
  return initial;
}

/** `GradeTabs` 등과 동일한 상한 — 등급 열 최대 개수 */
const MAX_ASSIGNABLE_GRADES = 11;

function buildInitialUnassigned(
  nurseDataWithRelation: NurseWithRelation[] | undefined
): NurseWithRelation[] {
  return (
    nurseDataWithRelation?.filter((nurse) => {
      const gId = nurse.grade ? `grade${nurse.grade}` : null;
      return !gId;
    }) || []
  );
}

export type GradeSettingContentsHandle = {
  triggerSave: () => void;
  triggerSaveDirect: () => void;
};

type GradeSettingContentsProps = {
  onClose: () => void;
  selectedGroupId: string;
  /** 상단 패널 저장 버튼 비활성화 등 — `GradeSettingModal` 에서 연결 */
  onMutationPendingChange?: (pending: boolean) => void;
  /** 변경사항 존재 여부를 상위 모달로 전달 */
  onDirtyChange?: (dirty: boolean) => void;
};

const GradeSettingContents = forwardRef<
  GradeSettingContentsHandle,
  GradeSettingContentsProps
>(function GradeSettingContents(
  { onClose, selectedGroupId, onMutationPendingChange, onDirtyChange },
  ref
) {
  const [isOpen, setOpen] = useState(false);
  const [isShiftConfigOpen, setShiftConfigOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const queryClient = useQueryClient();
  const throttle = useThrottle(1500);
  const isAdmin = useIsAdmin();

  const groupId = isAdmin ? selectedGroupId : "";

  const availableShifts = queryClient.getQueryData<type.res_shifts[]>([
    "SHIFT",
    selectedGroupId,
  ]);

  const { data: rosterConfig } = useRosterConfig();
  /** NurseSwapTables 등과 동일: 미정(undefined)이면 미드 노출 */
  const isUseMid = rosterConfig?.use_mid !== false;

  const { data: gradConfigData } = useGradeConfig({
    selectedGroupId: groupId,
  });

  const { data: nurseData } = useNurses(groupId);

  const nurseDataWithRelation = linkPreceptorRelations(nurseData!);

  const initialKeys = useMemo(() => {
    const configKeys = Object.keys(gradConfigData?.constraints || {});

    const nurseKeys =
      nurseDataWithRelation
        ?.map((n) => (n.grade ? `grade${n.grade}` : null))
        .filter((k): k is string => !!k) || [];

    const uniqueKeys = Array.from(new Set([...configKeys, ...nurseKeys]));

    if (uniqueKeys.length === 0) {
      const gn = gradConfigData?.grade_names;
      if (gn && typeof gn === "object" && Object.keys(gn).length > 0) {
        return Object.keys(gn)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => `grade${k}`);
      }
      return [];
    }

    return uniqueKeys.sort((a, b) => {
      const numA = parseInt(a.replace("grade", "") || "0", 10);
      const numB = parseInt(b.replace("grade", "") || "0", 10);
      return numA - numB;
    });
  }, [gradConfigData, nurseDataWithRelation]);

  const [shiftSettings, setShiftSettings] = useState<
    Record<string, GradeShiftCounts>
  >(() => {
    const settings = { ...(gradConfigData?.constraints || {}) } as Record<
      string,
      GradeShiftCounts
    >;
    initialKeys.forEach((key) => {
      if (!settings[key]) {
        settings[key] = { D: 0, E: 0, N: 0 };
      }
    });
    return settings;
  });

  const [grades, setGrades] = useState<Grade[]>(() => {
    return [
      { id: "total", label: "총 지정 인원", count: 0 },
      ...initialKeys.map((key) => ({
        id: key,
        label: `Grade ${key.replace("grade", "")}`,
        count: 0,
      })),
    ];
  });

  const [gradeNameMap, setGradeNameMap] = useState<GradeNamesMap>({});
  const [draftShiftSettings, setDraftShiftSettings] = useState<Record<
    string,
    GradeShiftCounts
  > | null>(null);
  const [draftGrades, setDraftGrades] = useState<Grade[] | null>(null);
  const [draftGradeNameMap, setDraftGradeNameMap] =
    useState<GradeNamesMap | null>(null);

  const gradeNamesSyncKey = JSON.stringify(gradConfigData?.grade_names ?? null);

  useEffect(() => {
    if (gradeNamesSyncKey === "null") {
      setGradeNameMap({});
      return;
    }
    try {
      const parsed = JSON.parse(gradeNamesSyncKey) as GradeNamesMap | null;
      if (parsed && typeof parsed === "object") {
        setGradeNameMap({ ...parsed });
      } else {
        setGradeNameMap({});
      }
    } catch {
      setGradeNameMap({});
    }
  }, [selectedGroupId, gradConfigData?.config_id, gradeNamesSyncKey]);

  const cloneShiftSettings = useCallback(
    (source: Record<string, GradeShiftCounts>) =>
      Object.fromEntries(
        Object.entries(source).map(([k, v]) => [k, { ...v }])
      ) as Record<string, GradeShiftCounts>,
    []
  );

  const [bundle, dispatchBundle] = useReducer(
    gradeBundleReducer,
    [initialKeys, nurseDataWithRelation] as const,
    ([keys, nurses]) => ({
      nursesByGrade: buildInitialNursesByGrade(keys, nurses),
      unassignedNurses: buildInitialUnassigned(nurses),
    })
  );
  const nursesByGrade = bundle.nursesByGrade;
  const unassignedNurses = bundle.unassignedNurses;

  const [highlightedNurseId, setHighlightedNurseId] = useState<string | null>(
    null
  );

  const [selectedLeftGradeId, setSelectedLeftGradeId] = useState<string | null>(
    "unassigned"
  );
  const [selectedRightGradeId, setSelectedRightGradeId] = useState<
    string | null
  >(null);

  const assignableGradeIds = useMemo(
    () => grades.filter((g) => g.id !== "total").map((g) => g.id),
    [grades]
  );

  useEffect(() => {
    if (assignableGradeIds.length === 0) {
      setSelectedRightGradeId(null);
      return;
    }
    setSelectedRightGradeId((prev) => {
      if (prev === "unassigned") return "unassigned";
      if (prev && assignableGradeIds.includes(prev)) return prev;
      return assignableGradeIds[0];
    });
  }, [assignableGradeIds]);

  useEffect(() => {
    if (
      selectedLeftGradeId !== null &&
      selectedLeftGradeId !== "unassigned" &&
      !assignableGradeIds.includes(selectedLeftGradeId)
    ) {
      setSelectedLeftGradeId("unassigned");
    }
  }, [assignableGradeIds, selectedLeftGradeId]);

  const prevGroupId = useRef(selectedGroupId);
  useEffect(() => {
    if (prevGroupId.current !== selectedGroupId) {
      onClose();
    }
    prevGroupId.current = selectedGroupId;
  }, [selectedGroupId, onClose]);

  useEffect(() => {
    setIsDirty(false);
  }, [selectedGroupId, gradConfigData?.config_id]);

  const displayGrades = useMemo(() => {
    const totalAssignedCount = Object.values(nursesByGrade).reduce(
      (sum, list) => sum + list.length,
      0
    );
    return grades.map((g) => {
      if (g.id === "total") return { ...g, count: totalAssignedCount };
      const n = g.id.replace("grade", "");
      const custom = gradeNameMap[n]?.trim();
      const label = custom && custom.length > 0 ? custom : `Grade ${n}`;
      return {
        ...g,
        label,
        count: nursesByGrade[g.id]?.length || 0,
      };
    });
  }, [grades, nursesByGrade, gradeNameMap]);

  const draftDisplayGrades = useMemo(() => {
    const sourceGrades = draftGrades ?? grades;
    const sourceNameMap = draftGradeNameMap ?? gradeNameMap;
    const totalAssignedCount = Object.values(nursesByGrade).reduce(
      (sum, list) => sum + list.length,
      0
    );
    return sourceGrades.map((g) => {
      if (g.id === "total") return { ...g, count: totalAssignedCount };
      const n = g.id.replace("grade", "");
      const custom = sourceNameMap[n]?.trim();
      const label = custom && custom.length > 0 ? custom : `Grade ${n}`;
      return {
        ...g,
        label,
        count: nursesByGrade[g.id]?.length || 0,
      };
    });
  }, [draftGradeNameMap, draftGrades, gradeNameMap, grades, nursesByGrade]);

  /** D/E/N(/M): 근무자 표와 동일하게 default_shift별 첫 행으로 shift_id·색 고정 */
  const constraintLetters = useMemo(
    (): readonly ShiftType[] =>
      isUseMid ? ["D", "E", "N", "M"] : ["D", "E", "N"],
    [isUseMid]
  );

  /** GET 직후 local state가 비어 있으면 위반/저장 검증이 어긋나므로 constraints 1회 병합 */
  const shiftSettingsHydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    shiftSettingsHydratedKeyRef.current = null;
  }, [selectedGroupId]);

  useEffect(() => {
    const incoming = gradConfigData?.constraints;
    if (!incoming || Object.keys(incoming).length === 0) return;

    const hydrateKey = `${selectedGroupId}:${
      gradConfigData?.config_id ?? "noid"
    }`;
    if (shiftSettingsHydratedKeyRef.current === hydrateKey) return;
    shiftSettingsHydratedKeyRef.current = hydrateKey;

    setShiftSettings((prev) => {
      const next = { ...prev };
      for (const gk of Object.keys(incoming)) {
        const row = incoming[gk];
        if (!row) continue;
        const base: GradeShiftCounts = isUseMid
          ? { D: 0, E: 0, N: 0, M: 0 }
          : { D: 0, E: 0, N: 0 };
        next[gk] = {
          ...base,
          ...(prev[gk] ?? {}),
          ...row,
        };
        if (isUseMid && next[gk].M === undefined) {
          next[gk] = { ...next[gk], M: Number(row.M) || 0 };
        }
      }
      return next;
    });
  }, [gradConfigData, selectedGroupId, isUseMid]);

  const shiftDisplayInfo = useMemo((): GradeShiftGridRowDisplay[] => {
    const FALLBACK_COLOR: Partial<Record<ShiftType, string>> = {
      D: "#59DDB7",
      E: "#72BFFF",
      N: "#BAB0F0",
      M: "#C4B5FD",
    };
    /** default_shift별 첫 시프트 행 — shift_id 미제공(API 보강 예정) 시 name → 문자(D/E/N/M) 순 폴백 */
    const labelByDefault = new Map<string, string>();
    const colorByDefault = new Map<string, string>();

    (availableShifts ?? []).forEach((s) => {
      const key = s.default_shift;
      if (!key || labelByDefault.has(key)) return;

      const sid = typeof s.shift_id === "string" ? s.shift_id.trim() : "";
      const nm = typeof s.name === "string" ? s.name.trim() : "";
      const label = sid !== "" ? sid : nm !== "" ? nm : key;

      labelByDefault.set(key, label);
      colorByDefault.set(key, s.color ?? "");
    });

    return constraintLetters.map((letter) => ({
      constraintKey: letter,
      displayId: labelByDefault.get(letter) ?? letter,
      color: colorByDefault.get(letter) || FALLBACK_COLOR[letter] || "#9CA3AF",
    }));
  }, [availableShifts, constraintLetters]);

  useEffect(() => {
    if (!isUseMid) return;
    setShiftSettings((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (next[k].M === undefined) {
          next[k] = { ...next[k], M: 0 };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isUseMid]);

  const totalStaffCount = useMemo(() => {
    const inGrades = Object.values(nursesByGrade).reduce(
      (sum, list) => sum + list.length,
      0
    );
    return inGrades + unassignedNurses.length;
  }, [nursesByGrade, unassignedNurses]);

  const modalGrades = draftGrades ?? grades;
  const modalShiftSettings = draftShiftSettings ?? shiftSettings;

  const gradeConfiguredTotal = useMemo(() => {
    return modalGrades
      .filter((g) => g.id !== "total")
      .reduce((sum, g) => {
        const s = modalShiftSettings[g.id];
        if (!s) return sum;
        return (
          sum +
          constraintLetters.reduce((acc, key) => acc + (Number(s[key]) || 0), 0)
        );
      }, 0);
  }, [modalGrades, modalShiftSettings, constraintLetters]);

  const gradeCoverageValidation = useMemo(
    () =>
      validateShiftCoverage({
        buckets: displayGrades
          .filter((g) => g.id !== "total")
          .map((g) => ({ id: g.id, label: g.label })),
        shiftSettings,
        nursesByBucket: nursesByGrade,
        shiftKeys: constraintLetters,
      }),
    [constraintLetters, displayGrades, nursesByGrade, shiftSettings]
  );

  const modalGradeCoverageValidation = useMemo(
    () =>
      validateShiftCoverage({
        buckets: draftDisplayGrades
          .filter((g) => g.id !== "total")
          .map((g) => ({ id: g.id, label: g.label })),
        shiftSettings: modalShiftSettings,
        nursesByBucket: nursesByGrade,
        shiftKeys: constraintLetters,
      }),
    [constraintLetters, draftDisplayGrades, modalShiftSettings, nursesByGrade]
  );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload: GradeSavePayload) => {
      const [gradeResponse, nursesResponse] = await Promise.all([
        httpClient.post("/grade/config", payload, {
          params: isAdmin ? { group_id: selectedGroupId } : undefined,
        }),
        httpClient.post("/nurses/bulk-update", payload.nurses, {
          params: isAdmin ? { group_id: selectedGroupId } : undefined,
        }),
      ]);

      return { gradeResponse, nursesResponse };
    },
    onSuccess: () => {
      toast.success("Grade 설정이 완료되었습니다.");
      setIsDirty(false);
      setOpen(false);
      onClose();
      // useGradeConfig / 목록 — 사이드프로필 `useNurseDetail`: ["NURSES","detail",id] 는 list 키와 별도라 상세도 무효화
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.grade.config(selectedGroupId),
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.list(groupId),
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_BASE_KEY.NURSES, "detail"],
        refetchType: "all",
      });
    },
  });

  const handleConfirmSave = useCallback(() => {
    const assigned = Object.entries(nursesByGrade).flatMap(([gradeKey, list]) =>
      unlinkPreceptorRelations(list).map((nurse) => ({
        ...nurse,
        grade: parseInt(gradeKey.replace("grade", ""), 10),
      }))
    );

    const unassigned = unlinkPreceptorRelations(unassignedNurses).map(
      (nurse) => ({
        ...nurse,
        grade: 0,
      })
    );

    const payloadSettings: GradeData = !isUseMid
      ? Object.fromEntries(
          Object.entries(shiftSettings).map(([gk, v]) => {
            const row = { ...v };
            delete row.M;
            return [gk, row];
          })
        )
      : shiftSettings;

    const { constraints } = transformGradeData(payloadSettings);

    const grade_names: GradeNamesMap | null = (() => {
      const out: GradeNamesMap = {};
      grades
        .filter((g) => g.id !== "total")
        .forEach((g) => {
          const n = g.id.replace("grade", "");
          const nm = gradeNameMap[n]?.trim();
          if (nm) out[n] = nm;
        });
      return Object.keys(out).length > 0 ? out : null;
    })();

    const payload: GradeSavePayload = {
      null_grade_policy: gradConfigData?.null_grade_policy ?? "LOWEST",
      use_dynamic_scaling: gradConfigData?.use_dynamic_scaling ?? false,
      constraints,
      grade_names,
      use_mid: gradConfigData?.use_mid ?? isUseMid,
      default_shifts: gradConfigData?.default_shifts ?? null,
      nurses: [...assigned, ...unassigned],
    };

    return mutateAsync(payload);
  }, [
    nursesByGrade,
    unassignedNurses,
    isUseMid,
    shiftSettings,
    gradConfigData?.null_grade_policy,
    gradConfigData?.use_dynamic_scaling,
    gradConfigData?.use_mid,
    gradConfigData?.default_shifts,
    grades,
    gradeNameMap,
    mutateAsync,
  ]);

  const validateBeforeSave = useCallback(() => {
    const errors = gradeCoverageValidation.messages;

    if (errors.length > 0) {
      return confirm(
        `근무 가능 인원 위반사항이 있습니다. \n그래도 저장하시겠습니까? \n\n${errors.join("\n")}`
      );
    }
    return true;
  }, [gradeCoverageValidation.messages]);

  const handleSaveClick = useCallback(() => {
    throttle(() => {
      if (!validateBeforeSave()) return;
      setOpen(true);
    });
  }, [throttle, validateBeforeSave]);

  const handleSaveDirect = useCallback(() => {
    if (!validateBeforeSave()) return;
    void handleConfirmSave();
  }, [validateBeforeSave, handleConfirmSave]);

  useImperativeHandle(
    ref,
    () => ({
      triggerSave: handleSaveClick,
      triggerSaveDirect: handleSaveDirect,
    }),
    [handleSaveClick, handleSaveDirect]
  );

  useEffect(() => {
    onMutationPendingChange?.(isPending);
  }, [isPending, onMutationPendingChange]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const selectIdForSideprofile = (bool: boolean, nurseID: string) => {
    setHighlightedNurseId(bool ? null : nurseID);
  };

  const isInvalidGrade = useCallback(
    (gradeId: string) => {
      return Boolean(gradeCoverageValidation.byBucket[gradeId]?.hasError);
    },
    [gradeCoverageValidation.byBucket]
  );

  const modalIsInvalidGrade = useCallback(
    (gradeId: string) => {
      return Boolean(modalGradeCoverageValidation.byBucket[gradeId]?.hasError);
    },
    [modalGradeCoverageValidation.byBucket]
  );

  const getInvalidGradeTooltip = useCallback(
    (gradeId: string): string | undefined => {
      const result = gradeCoverageValidation.byBucket[gradeId];
      if (!result?.hasError) return undefined;
      return `근무 가능 인원 부족\n${result.messages.join("\n")}`;
    },
    [gradeCoverageValidation.byBucket]
  );

  const openShiftConfigModal = () => {
    setDraftGrades(grades.map((g) => ({ ...g })));
    setDraftShiftSettings(cloneShiftSettings(shiftSettings));
    setDraftGradeNameMap({ ...gradeNameMap });
    setShiftConfigOpen(true);
  };

  const closeShiftConfigModal = () => {
    setShiftConfigOpen(false);
    setDraftGrades(null);
    setDraftShiftSettings(null);
    setDraftGradeNameMap(null);
  };

  const confirmShiftConfigModal = () => {
    if (!draftGrades || !draftShiftSettings || !draftGradeNameMap) {
      closeShiftConfigModal();
      return;
    }

    const gradesChanged =
      JSON.stringify(grades) !== JSON.stringify(draftGrades);
    const settingsChanged =
      JSON.stringify(shiftSettings) !== JSON.stringify(draftShiftSettings);
    const namesChanged =
      JSON.stringify(gradeNameMap) !== JSON.stringify(draftGradeNameMap);

    const prevGradeIds = new Set(
      grades.filter((g) => g.id !== "total").map((g) => g.id)
    );
    const nextGradeIds = new Set(
      draftGrades.filter((g) => g.id !== "total").map((g) => g.id)
    );
    const deletedGradeIds = Array.from(prevGradeIds).filter(
      (id) => !nextGradeIds.has(id)
    );

    setGrades(draftGrades);
    setShiftSettings(draftShiftSettings);
    setGradeNameMap(draftGradeNameMap);

    if (deletedGradeIds.length > 0) {
      dispatchBundle({
        type: "apply",
        fn: (state) => {
          const nursesByGrade = { ...state.nursesByGrade };
          const movedToUnassigned: NurseWithRelation[] = [];

          deletedGradeIds.forEach((gradeId) => {
            const from = nursesByGrade[gradeId] ?? [];
            if (from.length > 0) {
              movedToUnassigned.push(
                ...from.map((n) => ({
                  ...n,
                  grade: 0,
                }))
              );
            }
            delete nursesByGrade[gradeId];
          });

          nextGradeIds.forEach((gradeId) => {
            if (!nursesByGrade[gradeId]) {
              nursesByGrade[gradeId] = [];
            }
          });

          return {
            nursesByGrade,
            unassignedNurses: [...state.unassignedNurses, ...movedToUnassigned],
          };
        },
      });
    }

    const validGradeIds = new Set(
      draftGrades.filter((g) => g.id !== "total").map((g) => g.id)
    );
    setSelectedLeftGradeId((prev) => {
      if (prev === null || prev === "unassigned") return prev;
      return validGradeIds.has(prev) ? prev : "unassigned";
    });
    setSelectedRightGradeId((prev) => {
      if (prev === null || prev === "unassigned") return prev;
      return validGradeIds.has(prev) ? prev : null;
    });

    if (gradesChanged || settingsChanged || namesChanged) {
      setIsDirty(true);
    }
    closeShiftConfigModal();
  };

  const handleDraftAddGrade = () => {
    const sourceGrades = draftGrades ?? grades;
    const sourceNameMap = draftGradeNameMap ?? gradeNameMap;
    const assignable = sourceGrades.filter((g) => g.id !== "total");
    if (assignable.length >= MAX_ASSIGNABLE_GRADES) {
      toast.error(
        `Grade는 최대 ${MAX_ASSIGNABLE_GRADES}개까지 추가할 수 있습니다.`
      );
      return;
    }

    const nums = assignable.map((g) => parseInt(g.id.replace("grade", ""), 10));
    const nextNum = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    const newId = `grade${nextNum}`;

    const input = window.prompt(
      "새 Grade 표시 이름을 입력하세요.",
      `Grade ${nextNum}`
    );
    if (input === null) return;
    const trimmed = input.trim();
    if (!trimmed) {
      toast.error("이름을 입력해주세요.");
      return;
    }

    setDraftGrades((prev) => {
      const base = prev ?? sourceGrades;
      const total = base.find((g) => g.id === "total")!;
      const rest = base.filter((g) => g.id !== "total");
      const appended = [...rest, { id: newId, label: trimmed, count: 0 }].sort(
        (a, b) => {
          const na = parseInt(a.id.replace("grade", ""), 10);
          const nb = parseInt(b.id.replace("grade", ""), 10);
          return na - nb;
        }
      );
      return [total, ...appended];
    });

    setDraftGradeNameMap((prev) => ({
      ...(prev ?? sourceNameMap),
      [String(nextNum)]: trimmed,
    }));

    setDraftShiftSettings((prev) => ({
      ...(prev ?? cloneShiftSettings(shiftSettings)),
      [newId]: isUseMid ? { D: 0, E: 0, N: 0, M: 0 } : { D: 0, E: 0, N: 0 },
    }));
  };

  const handleDraftRenameGrade = (gradeId: string, currentLabel: string) => {
    const next = window.prompt("표시 이름을 입력하세요.", currentLabel);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed) {
      toast.error("이름을 입력해주세요.");
      return;
    }
    const n = gradeId.replace("grade", "");
    setDraftGradeNameMap((prev) => ({
      ...(prev ?? { ...gradeNameMap }),
      [n]: trimmed,
    }));
  };

  const handleDraftDeleteGrade = (gradeId: string) => {
    const sourceGrades = draftGrades ?? grades;
    const assignable = sourceGrades.filter((g) => g.id !== "total");
    if (assignable.length <= MIN_ASSIGNABLE_GRADES_COUNT) {
      toast.error(
        `Grade는 최소 ${MIN_ASSIGNABLE_GRADES_COUNT}개가 필요합니다.`
      );
      return;
    }
    const moving = nursesByGrade[gradeId] ?? [];
    if (moving.length > 0) {
      const ok = window.confirm(
        `해당 Grade에 배정된 간호사 ${moving.length}명이 미지정으로 이동합니다. 계속할까요?`
      );
      if (!ok) return;
    }

    setDraftGrades((prev) =>
      (prev ?? sourceGrades).filter((g) => g.id !== gradeId)
    );
    setDraftShiftSettings((prev) => {
      const next = { ...(prev ?? cloneShiftSettings(shiftSettings)) };
      delete next[gradeId];
      return next;
    });
    const num = gradeId.replace("grade", "");
    setDraftGradeNameMap((prev) => {
      const copy = { ...(prev ?? { ...gradeNameMap }) };
      delete copy[num];
      return copy;
    });
  };

  const handleDraftMinStaffChange = (
    gradeId: string,
    shift: ShiftType,
    value: string
  ) => {
    const newCount =
      value === "" ? null : Math.max(0, parseInt(value, 10) || 0);

    setDraftShiftSettings((prev) => {
      const base = prev ?? cloneShiftSettings(shiftSettings);
      return {
        ...base,
        [gradeId]: {
          ...(base[gradeId] ??
            (isUseMid ? { D: 0, E: 0, N: 0, M: 0 } : { D: 0, E: 0, N: 0 })),
          [shift]: newCount,
        },
      };
    });
  };

  const handleMoveNurses = (
    nurseIds: string[],
    fromKey: string,
    toKey: string
  ) => {
    if (!nurseIds.length || fromKey === toKey) return;
    dispatchBundle({ type: "move_nurses", nurseIds, fromKey, toKey });
    setIsDirty(true);
  };

  return (
    <>
      <CustomModal.Confirm
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirmSave}
        isPending={isPending}
        pendingMsg={MSG.CONFIG_SETTING}
        title="Grade 설정을 저장하시겠습니까?"
      />
      <GradeShiftConfigModal
        isOpen={isShiftConfigOpen}
        onClose={closeShiftConfigModal}
        onConfirm={confirmShiftConfigModal}
        displayGrades={draftDisplayGrades}
        shiftSettings={modalShiftSettings}
        shiftDisplayInfo={shiftDisplayInfo}
        totalStaffCount={totalStaffCount}
        gradeConfiguredTotal={gradeConfiguredTotal}
        unassignedCount={unassignedNurses.length}
        isInvalidGrade={modalIsInvalidGrade}
        onMinStaffChange={handleDraftMinStaffChange}
        onRenameGrade={handleDraftRenameGrade}
        onDeleteGrade={handleDraftDeleteGrade}
        onAddGrade={handleDraftAddGrade}
        addGradeDisabled={
          (draftGrades ?? grades).filter((g) => g.id !== "total").length >=
          MAX_ASSIGNABLE_GRADES
        }
      />
      <div>
        <GridModalTitle title="Grade 설정" />

        <div className="flex flex-col w-full  mx-auto px-10">
          <GridModalHeader>
            <p>*근무자 관리 페이지에서 근무자 개별 Grade 설정가능</p>

            <div className="flex justify-end min-w-0">
              <ActionButton
                className="text-foreground"
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade setting"
                    className=" transition-all group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"
                  />
                }
                onClick={openShiftConfigModal}
              >
                Shift 인원 설정/관리
              </ActionButton>
            </div>
          </GridModalHeader>
          <div className="mb-5 scrollbar-float overflow-auto">
            <div className=" ">
              <GradeTransferTables
                gradeBuckets={displayGrades.filter((g) => g.id !== "total")}
                nursesByGrade={nursesByGrade}
                unassignedNurses={unassignedNurses}
                invalidGradeTooltip={getInvalidGradeTooltip}
                selectedLeftGradeId={selectedLeftGradeId}
                selectedRightGradeId={selectedRightGradeId}
                onLeftGradeChange={setSelectedLeftGradeId}
                onRightGradeChange={setSelectedRightGradeId}
                onMoveNurses={handleMoveNurses}
                selectedGroupId={selectedGroupId}
                activeNurseIdForSideProfile={highlightedNurseId}
                onNurseClick={(id) =>
                  selectIdForSideprofile(highlightedNurseId === id, id)
                }
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default GradeSettingContents;
